/**
 * FeatureNavBar — unified app module navigation (F11).
 * Mobile: 3-column × 2-row grid. Desktop: horizontal scroll row.
 */
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FEATURE_NAV, isFeatureNavActive } from '../../config/featureNav';
import { cn } from '../../lib/utils';

interface FeatureNavBarProps {
  className?: string;
  /** Slightly softer active state for legacy Layout variants */
  variant?: 'default' | 'soft';
}

export const FeatureNavBar: React.FC<FeatureNavBarProps> = ({
  className,
  variant = 'default',
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const activeClass =
    variant === 'soft'
      ? 'bg-sky-100 text-sky-700 shadow-sm'
      : 'bg-sky-600 text-white shadow-md';

  const inactiveClass =
    variant === 'soft'
      ? 'text-slate-600 hover:text-sky-700 hover:bg-sky-50'
      : 'text-slate-700 hover:bg-sky-100 hover:text-sky-700';

  return (
    <div className={cn('bg-white/95 backdrop-blur-sm border-b border-slate-200', className)}>
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <nav
          className="grid grid-cols-3 grid-rows-2 gap-1.5 py-2 md:flex md:flex-nowrap md:items-center md:gap-2 md:py-2.5 md:overflow-x-auto scrollbar-thin"
          data-testid="feature-nav"
        >
          {FEATURE_NAV.map(({ label, path, emoji, tooltip, testId }) => {
            const isActive = isFeatureNavActive(location.pathname, path);

            return (
              <button
                key={label}
                type="button"
                onClick={() => navigate(path)}
                title={tooltip}
                className={cn(
                  'group relative flex flex-col md:flex-row items-center justify-center md:justify-start',
                  'gap-0.5 md:gap-2 px-1.5 py-2 md:px-4 md:py-2 rounded-lg',
                  'text-[10px] sm:text-[11px] md:text-sm font-semibold leading-tight text-center md:text-left',
                  'transition-all md:flex-shrink-0 md:whitespace-nowrap',
                  isActive ? activeClass : inactiveClass
                )}
                data-testid={testId || `nav-${label.toLowerCase()}`}
              >
                <span className="text-base md:text-base leading-none">{emoji}</span>
                <span className="truncate max-w-full">{label}</span>
                <span className="absolute left-1/2 -translate-x-1/2 top-full mt-2 px-2.5 py-1.5 bg-slate-800 text-white text-xs rounded-lg whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 pointer-events-none shadow-lg hidden md:block">
                  {tooltip}
                </span>
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );
};

export default FeatureNavBar;
